import React, { useState } from "react";
import { chordFor, chordRecipe } from "../../lib/numberSenseData";
import {
  chordsForProgression,
  shellVoicing,
  notesFor,
  scaleFor,
  playScale,
  audioSupported,
} from "../../lib/keyboardPlan";
import { Icon, Keys, Piano, PlayButton } from "../numberSenseUi";

const CAN_PLAY = audioSupported();
const EMPTY_SET = new Set();
const noop = () => {};

// "hear the scale" — plays the key's scale ascending, with a brief playing state.
const ScalePlayButton = ({ scaleKey }) => {
  const [playing, setPlaying] = useState(false);
  const run = () => {
    playScale(scaleKey);
    setPlaying(true);
    window.setTimeout(() => setPlaying(false), 8 * 300);
  };
  return (
    <button type="button" className={`ns-play ${playing ? "on" : ""}`} onClick={run}>
      <Icon name="play" />
      <span>hear the scale</span>
    </button>
  );
};

// The scale slide's example: the seven scale notes as numbered chips + audio.
const ScaleView = ({ scaleKey }) => {
  const notes = scaleFor(scaleKey) || [];
  const minor = scaleKey.endsWith("m");
  return (
    <div className="teach-ex">
      <div className="teach-chips">
        {notes.map((n, i) => (
          <span className="teach-chip" key={`${n}-${i}`}>
            <b className="mono">{i + 1}</b>
            <span>{n}</span>
          </span>
        ))}
      </div>
      <div className="teach-ex-foot">
        <span className="teach-ex-key">
          {scaleKey} {minor ? "minor" : "major"} scale
        </span>
        {CAN_PLAY && <ScalePlayButton scaleKey={scaleKey} />}
      </div>
    </div>
  );
};

// The chord-construction slide: the chord highlighted on a read-only keyboard,
// the interval recipe spelled note→+steps→note, and a "hear it" button. Teaches
// *how* a chord is physically stacked, not just which number it is.
const RecipeView = ({ recipe }) => {
  const r = chordRecipe(recipe.key, recipe.deg);
  if (!r) return null;
  return (
    <div className="teach-ex">
      <Piano pressed={EMPTY_SET} onPress={noop} hint={r.notes} disabled />
      <div className="teach-recipe" aria-label={`${r.label}: ${r.notes.join(" ")}`}>
        {r.notes.map((n, i) => (
          <React.Fragment key={`${n}-${i}`}>
            <span className="teach-note">{n}</span>
            {i < r.steps.length && (
              <span className="teach-step mono">+{r.steps[i]}</span>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="teach-ex-foot">
        <span className="teach-ex-key">
          {r.label} · {r.key} {r.deg}
        </span>
        {CAN_PLAY && (
          <PlayButton chords={[r.notes]} countIn={false} label="hear it" />
        )}
      </div>
    </div>
  );
};

// The optional musical example on a slide: a row of degree→chord chips and a
// "hear it" button that plays the chords in time. When `voicing` is set the
// example teaches the rootless *shell* (guide tones): each chip also spells the
// shell notes and "hear it" plays them — so a slide that says "I (C–E–G)
// becomes E–G" actually shows and sounds E–G, not the full chord.
const Example = ({ example }) => {
  const { key, degrees, voicing } = example;
  // Every chip spells its notes; a voicing example shows the rootless shell
  // (guide tones) instead, so "I (C–E–G) becomes E–G" shows and plays E–G.
  const notesOf = (deg) => (voicing ? shellVoicing(key, deg) : notesFor(key, deg) || []);
  return (
    <div className="teach-ex">
      <div className="teach-chips">
        {degrees.map((deg, i) => (
          <span className="teach-chip" key={`${deg}-${i}`}>
            <b className="mono">{deg}</b>
            <span>{chordFor(key, deg)}</span>
            <span className="teach-chip-notes mono">{notesOf(deg).join(" ")}</span>
          </span>
        ))}
      </div>
      <div className="teach-ex-foot">
        <span className="teach-ex-key">in {key}</span>
        {CAN_PLAY && (
          <PlayButton
            chords={
              voicing
                ? degrees.map((deg) => notesOf(deg))
                : chordsForProgression(key, degrees)
            }
            label="hear it"
          />
        )}
      </div>
    </div>
  );
};

/* ============================================================
   Number Sense — a short "learn" intro: a few swipeable concept
   slides shown before a unit's first lesson and reused, read-only,
   in the Learn library. A slide may carry a musical example (chord
   chips + a "hear it" button) so the teaching isn't text-only.

   Props:
     title   : string            what's being taught (unit / concept name)
     slides  : [{ title, body, example? }]
     cta     : string            label for the final button
     onDone  : () => void        fired from the final slide's CTA
     onClose : () => void        bail out (the × / back)
   ============================================================ */
const TeachScreen = ({ title, slides = [], cta = "got it", onDone, onClose }) => {
  const [i, setI] = useState(0);
  const slide = slides[i] || { title: "", body: "" };
  const last = i >= slides.length - 1;

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onClose} aria-label="close">
            <Icon name="back" />
          </button>
          <div className="subbar-title">{title}</div>
        </div>
        <div className="scroll">
          <div className="teach-card">
            <div className="teach-kicker">
              <Icon name="book" />
              <span>learn</span>
            </div>
            <h2 className="teach-title">{slide.title}</h2>
            <p className="teach-body">{slide.body}</p>
            {slide.scale && <ScaleView scaleKey={slide.scale.key} />}
            {slide.recipe && <RecipeView recipe={slide.recipe} />}
            {slide.example && <Example example={slide.example} />}
          </div>

          {slides.length > 1 && (
            <div className="teach-dots" aria-hidden="true">
              {slides.map((s, idx) => (
                <i key={s.title} className={idx === i ? "on" : ""} />
              ))}
            </div>
          )}

          <div className="teach-nav">
            {i > 0 ? (
              <button className="btn ghost" onClick={() => setI(i - 1)}>
                back
              </button>
            ) : (
              <span />
            )}
            {last ? (
              <button className="btn" onClick={onDone}>
                {cta}
              </button>
            ) : (
              <button className="btn" onClick={() => setI(i + 1)}>
                next
              </button>
            )}
          </div>

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeachScreen;
